import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthorizationService } from '../authorization/authorization.service';
import { IncidentReportRequest } from '../../shared/models/incidentReportRequest';
import { IncidentReportResponse } from '../../shared/models/incidentReportResponse';

export interface IncidentScreenshotPayload {
  name: string;
  base64: string;
  previewUrl: string;
}

export interface IncidentAccountOption {
  accountNumber: string;
  thirdPartyId: string;
}

export interface IncidentRecord {
  incidentID: number;
  agentID: string;
  agentEmail?: string;
  pageName: string;
  country: string;
  title: string;
  usernameOfAffectedCustomer: string;
  accountNumber: string;
  thirdParty: string;
  agentNameShortId: string;
  websitePageScreen: string;
  detailedDescription: string;
  screenshotAttachment: string;
  screenshotPreviews: string[];
  screenshots: IncidentScreenshotPayload[];
  pegaVerificationCompleted: string;
  additionalComments: string;
  customersImpacted: string;
  developerResolution: string;
  status: string;
  resolution: string;
  createdDate: string;
  updatedDate: string;
  closedDate: string;
}

@Injectable({ providedIn: 'root' })
export class IncidentManagementService {
  currentCountry: string;
  private readonly dateFormatter = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  private readonly timeFormatter = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  constructor(private auth: AuthorizationService) {}

  getAccountNumbersForUser(userId: string): Observable<IncidentAccountOption[]> {
    const normalizedUserId = (userId || '').trim();
    if (!normalizedUserId) {
      return of([]);
    }

    return this.auth.getAccountNbrList(normalizedUserId).pipe(
      map((response: any) => this.extractAccountOptions(response)),
      catchError(() => of([]))
    );
  }

  getIncidents(): Observable<IncidentRecord[]> {
    const language = (window as any).mbfs.language;
    this.currentCountry = (window as any).mbfs.country;

    return this.auth.getIncidentData({
      includeClosed: true,
      userId: sessionStorage.getItem('username'),
      password: "",
      country: this.currentCountry,
      language: language,
    }).pipe(
      map((response: IncidentReportResponse[] | null | undefined) => {
        if (!Array.isArray(response) || !response.length) {
          return [];
        }
        return response.map((item) => this.mapResponseToRecord(item));
      }),
      catchError(() => of([]))
    );
  }

  createIncident(record: IncidentRecord): Observable<IncidentRecord | null> {
    const requestBody: IncidentReportRequest[] = [this.mapRecordToRequest(record)];

    return this.auth.saveIncidentData(requestBody).pipe(
      switchMap((saved: boolean) => {
        if (!saved) {
          return of(null);
        }

        return this.getIncidents().pipe(
          map((latestRecords) => {
            const createdRecord = this.resolveCreatedRecord(latestRecords, record);
            return createdRecord ? this.mergeScreenshotFallback(createdRecord, record) : record;
          })
        );
      }),
      catchError(() => of(null))
    );
  }

  updateIncident(record: IncidentRecord): Observable<IncidentRecord | null> {
    const requestBody: IncidentReportRequest[] = [this.mapRecordToRequest(record)];

    return this.auth.saveIncidentData(requestBody).pipe(
      switchMap((saved: boolean) => {
        if (!saved) {
          return of(record);
        }

        return this.getIncidents().pipe(
          map((latestRecords) => {
            const latest = latestRecords.find((item) => item.incidentID === record.incidentID) || record;
            return this.mergeScreenshotFallback(latest, record);
          })
        );
      }),
      catchError(() => of(record))
    );
  }

  private resolveCreatedRecord(records: IncidentRecord[], fallback: IncidentRecord): IncidentRecord | null {
    if (!records.length) {
      return null;
    }

    const exact = records.find((item) =>
      (item.usernameOfAffectedCustomer || '').trim() === (fallback.usernameOfAffectedCustomer || '').trim() &&
      (item.accountNumber || '').trim() === (fallback.accountNumber || '').trim() &&
      (item.title || '').trim() === (fallback.title || '').trim() &&
      (item.websitePageScreen || '').trim() === (fallback.websitePageScreen || '').trim()
    );

    if (exact) {
      return exact;
    }

    const byId = [...records].sort((a, b) => (Number(b.incidentID) || 0) - (Number(a.incidentID) || 0));
    return byId[0] || null;
  }

  private mergeScreenshotFallback(target: IncidentRecord, source: IncidentRecord): IncidentRecord {
    const hasPreview = Array.isArray(target.screenshotPreviews) && target.screenshotPreviews.some((item) => !!item);
    const hasPayload = Array.isArray(target.screenshots) && target.screenshots.some((item) => !!item.base64 || !!item.previewUrl);

    if (hasPreview || hasPayload) {
      return target;
    }

    return {
      ...target,
      screenshotAttachment: target.screenshotAttachment || source.screenshotAttachment,
      screenshotPreviews: source.screenshotPreviews || [],
      screenshots: source.screenshots || []
    };
  }

  private mapRecordToRequest(record: IncidentRecord): IncidentReportRequest {
    const screenshot1 = record.screenshots[0];
    const screenshot2 = record.screenshots[1];
    const screenshot3 = record.screenshots[2];
    const resolvedAgentEmail = this.resolveAgentEmail();

    return {
      incidentId: record.incidentID > 0 ? record.incidentID : undefined,
      customerUsername: record.usernameOfAffectedCustomer || '',
      accountNumber: record.accountNumber || '',
      thirdParty: record.thirdParty || '',
      agentNameShortId: record.agentNameShortId || '',
      agentEmail: resolvedAgentEmail,
      websitePage: record.websitePageScreen || record.pageName || '',
      country: record.country || '',
      incidentTitle: record.title || '',
      detailedDescription: record.detailedDescription || '',
      pegaVerificationCompleted: this.toPegaFlag(record.pegaVerificationCompleted),
      additionalComments: record.additionalComments || '',
      customersImpacted: Number(record.customersImpacted || 0),
      status: this.normalizeStatus(record.status),
      developerResolution: record.developerResolution || '',
      screenshot1Base64: screenshot1 ? this.stripDataUrlPrefix(screenshot1.base64) : undefined,
      screenshot1Name: screenshot1 ? screenshot1.name : undefined,
      screenshot2Base64: screenshot2 ? this.stripDataUrlPrefix(screenshot2.base64) : undefined,
      screenshot2Name: screenshot2 ? screenshot2.name : undefined,
      screenshot3Base64: screenshot3 ? this.stripDataUrlPrefix(screenshot3.base64) : undefined,
      screenshot3Name: screenshot3 ? screenshot3.name : undefined
    };
  }

  private mapResponseToRecord(item: IncidentReportResponse): IncidentRecord {
    let screenshots: IncidentScreenshotPayload[] | undefined;
    let screenshotPreviews: string[] | undefined;
    const getScreenshots = (): IncidentScreenshotPayload[] => {
      // Convert image payloads only when a visible row or the incident form needs them.
      screenshots ??= this.buildScreenshots(item);
      return screenshots;
    };

    return {
      incidentID: Number(item.incidentId || 0),
      agentID: this.extractShortId(item.agentNameShortId || ''),
      agentEmail: (item.agentEmail || '').trim(),
      pageName: item.websitePage || '',
      country: item.country || '',
      title: item.incidentTitle || '',
      usernameOfAffectedCustomer: item.customerUsername || '',
      accountNumber: item.accountNumber || '',
      thirdParty: item.thirdParty || '',
      agentNameShortId: item.agentNameShortId || '',
      websitePageScreen: item.websitePage || '',
      detailedDescription: item.detailedDescription || '',
      get screenshotAttachment() {
        return getScreenshots().map((screenshot) => screenshot.name).filter((name) => !!name).join(', ');
      },
      get screenshotPreviews() {
        screenshotPreviews ??= getScreenshots().map((screenshot) => screenshot.previewUrl);
        return screenshotPreviews;
      },
      get screenshots() {
        return getScreenshots();
      },
      pegaVerificationCompleted: this.fromPegaFlag(item.pegaVerificationCompleted),
      additionalComments: item.additionalComments || '',
      customersImpacted: String(item.customersImpacted ?? ''),
      developerResolution: item.developerResolution || '',
      status: item.status || 'Open',
      resolution: '',
      createdDate: this.normalizeDateForDisplay(item.createdDate),
      updatedDate: this.normalizeDateForDisplay(item.updatedDate),
      closedDate: this.normalizeDateForDisplay(item.closedDate)
    };
  }

  private buildScreenshots(item: IncidentReportResponse): IncidentScreenshotPayload[] {
    const responseAny = item as any;
    const candidates = [
      {
        name: item.screenshot1Name || '',
        rawBase64: this.normalizeScreenshotPayload(item.screenshot1Base64) || this.normalizeScreenshotPayload(responseAny.screenshot1)
      },
      {
        name: item.screenshot2Name || '',
        rawBase64: this.normalizeScreenshotPayload(item.screenshot2Base64) || this.normalizeScreenshotPayload(responseAny.screenshot2)
      },
      {
        name: item.screenshot3Name || '',
        rawBase64: this.normalizeScreenshotPayload(item.screenshot3Base64) || this.normalizeScreenshotPayload(responseAny.screenshot3)
      }
    ];

    return candidates
      .filter((candidate) => !!candidate.name || !!candidate.rawBase64)
      .map((candidate, index) => {
        const previewUrl = candidate.rawBase64 ? this.toDataUrl(candidate.rawBase64, candidate.name) : '';
        return {
          name: candidate.name || `screenshot-${index + 1}`,
          base64: previewUrl || candidate.rawBase64,
          previewUrl
        };
      });
  }

  private normalizeScreenshotPayload(payload: any): string {
    if (!payload) {
      return '';
    }

    if (typeof payload === 'string') {
      return payload.trim();
    }

    if (Array.isArray(payload)) {
      const bytes = payload
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));
      if (!bytes.length) {
        return '';
      }
      return this.byteArrayToBase64(bytes);
    }

    if (typeof payload === 'object') {
      if (typeof payload.base64 === 'string') {
        return payload.base64.trim();
      }

      if (payload.data) {
        return this.normalizeScreenshotPayload(payload.data);
      }
    }

    return '';
  }

  private byteArrayToBase64(bytes: number[]): string {
    if (!bytes.length) {
      return '';
    }

    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.slice(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return btoa(binary);
  }

  private toDataUrl(rawBase64: string, fileName: string): string {
    if (!rawBase64) {
      return '';
    }

    if (rawBase64.startsWith('data:')) {
      return rawBase64;
    }

    const lowered = (fileName || '').toLowerCase();
    if (lowered.endsWith('.jpg') || lowered.endsWith('.jpeg')) {
      return `data:image/jpeg;base64,${rawBase64}`;
    }
    if (lowered.endsWith('.gif')) {
      return `data:image/gif;base64,${rawBase64}`;
    }
    if (lowered.endsWith('.webp')) {
      return `data:image/webp;base64,${rawBase64}`;
    }

    return `data:image/png;base64,${rawBase64}`;
  }

  private stripDataUrlPrefix(value: string): string {
    if (!value) {
      return '';
    }

    const delimiterIndex = value.indexOf('base64,');
    if (delimiterIndex > -1) {
      return value.substring(delimiterIndex + 7);
    }

    return value;
  }

  private toPegaFlag(value: string): 'Y' | 'N' {
    return (value || '').toLowerCase() === 'yes' ? 'Y' : 'N';
  }

  private fromPegaFlag(value?: string): string {
    const normalized = (value || '').toUpperCase();
    return normalized === 'Y' || normalized === 'YES' ? 'Yes' : 'No';
  }

  private normalizeStatus(value: string): 'Open' | 'In Progress' | 'Closed' | 'Cancel' {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'in progress') {
      return 'In Progress';
    }
    if (normalized === 'closed') {
      return 'Closed';
    }
    if (normalized === 'cancel' || normalized === 'canceled' || normalized === 'cancelled') {
      return 'Cancel';
    }
    return 'Open';
  }

  private normalizeDateForDisplay(value?: string | number[]): string {
    if (!value) {
      return '';
    }

    if (Array.isArray(value)) {
      const year = Number(value[0]);
      const monthIndex = Number(value[1]) - 1;
      const day = Number(value[2]);
      const hour = Number(value[3] || 0);
      const minute = Number(value[4] || 0);
      const second = Number(value[5] || 0);
      const millisecond = Math.floor(Number(value[6] || 0) / 1000000);

      const arrayDate = new Date(year, monthIndex, day, hour, minute, second, millisecond);
      if (Number.isNaN(arrayDate.getTime())) {
        return value.join(',');
      }

      return this.formatDateTime(arrayDate);
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }

    return this.formatDateTime(parsedDate);
  }

  private formatDateTime(date: Date): string {
    const datePortion = this.dateFormatter.format(date).replace(/ /g, '-');
    const timePortion = this.timeFormatter.format(date);

    return `${datePortion} ${timePortion}`;
  }

  private extractShortId(agentNameShortId: string): string {
    const rawValue = (agentNameShortId || '').trim();
    if (!rawValue) {
      return '';
    }

    const splitBySlash = rawValue.split('/');
    if (splitBySlash.length > 1) {
      return splitBySlash[splitBySlash.length - 1].trim();
    }

    return rawValue;
  }

  private resolveAgentEmail(): string {
    const sessionEmail = sessionStorage.getItem('email');
    return (sessionEmail || '').trim();
  }

  private extractAccountOptions(response: any): IncidentAccountOption[] {
    if (!response) {
      return [];
    }

    const responseLevelThirdPartyId = this.extractThirdPartyId(response);

    const candidates = [
      response,
      response.accountNbrList,
      response.accountNumberList,
      response.interestLetterCcanList,
      response.interestLetterCcanListResponseBean,
      response.data,
      response.response
    ];

    for (const candidate of candidates) {
      const extracted = this.collectAccountOptionsFromCandidate(candidate, responseLevelThirdPartyId);
      if (extracted.length) {
        return extracted;
      }
    }

    return [];
  }

  private collectAccountOptionsFromCandidate(candidate: any, fallbackThirdPartyId = ''): IncidentAccountOption[] {
    if (!candidate) {
      return [];
    }

    if (Array.isArray(candidate)) {
      const options = candidate
        .map((item) => this.pickAccountOption(item, fallbackThirdPartyId))
        .filter((item) => !!item.accountNumber);

      const uniqueByAccount = new Map<string, IncidentAccountOption>();
      options.forEach((option) => {
        if (!uniqueByAccount.has(option.accountNumber)) {
          uniqueByAccount.set(option.accountNumber, option);
          return;
        }

        const current = uniqueByAccount.get(option.accountNumber);
        if (current && !current.thirdPartyId && option.thirdPartyId) {
          uniqueByAccount.set(option.accountNumber, option);
        }
      });

      return Array.from(uniqueByAccount.values());
    }

    if (typeof candidate === 'string') {
      const value = candidate.trim();
      return value ? [{ accountNumber: value, thirdPartyId: fallbackThirdPartyId }] : [];
    }

    if (typeof candidate === 'object') {
      const objectLevelThirdPartyId = this.extractThirdPartyId(candidate) || fallbackThirdPartyId;
      const direct = this.pickAccountOption(candidate, objectLevelThirdPartyId);
      if (direct.accountNumber) {
        return [direct];
      }

      const nestedArrayKey = Object.keys(candidate).find((key) => Array.isArray(candidate[key]));
      if (nestedArrayKey) {
        return this.collectAccountOptionsFromCandidate(candidate[nestedArrayKey], objectLevelThirdPartyId);
      }
    }

    return [];
  }

  private pickAccountOption(item: any, fallbackThirdPartyId = ''): IncidentAccountOption {
    if (!item) {
      return { accountNumber: '', thirdPartyId: '' };
    }

    if (typeof item === 'string') {
      return { accountNumber: item.trim(), thirdPartyId: fallbackThirdPartyId };
    }

    const accountNumber = item.accountNbr || item.accountNumber || item.ccanId || item.contractNum;
    const thirdPartyId = this.extractThirdPartyId(item) || fallbackThirdPartyId;

    return {
      accountNumber: accountNumber ? String(accountNumber).trim() : '',
      thirdPartyId: thirdPartyId ? String(thirdPartyId).trim() : ''
    };
  }

  private extractThirdPartyId(source: any): string {
    if (!source || typeof source !== 'object') {
      return '';
    }

    const rawThirdPartyId = source.thirdPartyId
      || source.thirdPartyID
      || source.thirdpartyId
      || source.thirdpartyID;

    return rawThirdPartyId ? String(rawThirdPartyId).trim() : '';
  }
}
